import { getPrimaryColor, JEJURO } from '@orcar/common-d2c';
import { useState } from 'react';
import styled from 'styled-components';

// 인플루언서 할인코드 — 고시제 하한선 때문에 가격은 깎지 않는다.
// 코드가 맞으면 "반납 후 주유권 지급" 대상으로 표시만 하고(예약 메모에 태그),
// 실제 주유권은 반납 후 실무자가 보낸다.
const INFLUENCER_CODES = ['제주에디'];

type InfluencerCodeAreaProps = {
  /** 적용된 코드(없으면 ''). 결제 확정 시 메모 태그로 전달된다. */
  onChange: (code: string) => void;
};

/**
 * 가격 변동 없는 "주유권 대상" 코드 입력.
 * 적용해도 결제금액은 그대로이며, 반납 후 주유권을 받는다고 안내한다.
 */
const InfluencerCodeArea = ({ onChange }: InfluencerCodeAreaProps) => {
  const [value, setValue] = useState('');
  const [applied, setApplied] = useState('');
  const [error, setError] = useState(false);

  const apply = () => {
    const code = value.trim();
    if (INFLUENCER_CODES.includes(code)) {
      setApplied(code);
      setError(false);
      onChange(code);
    } else {
      setApplied('');
      setError(code.length > 0);
      onChange('');
    }
  };

  const clear = () => {
    setApplied('');
    setValue('');
    setError(false);
    onChange('');
  };

  return (
    <Wrapper>
      <Label>할인코드</Label>
      {applied ? (
        <AppliedRow>
          <AppliedCode>{applied}</AppliedCode>
          <ClearButton type='button' onClick={clear}>
            취소
          </ClearButton>
        </AppliedRow>
      ) : (
        <InputRow>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder='할인코드를 입력하세요'
            onKeyDown={(e) => {
              if (e.key === 'Enter') apply();
            }}
          />
          <ApplyButton type='button' onClick={apply}>
            적용
          </ApplyButton>
        </InputRow>
      )}
      {error && (
        <ErrorText>등록된 코드가 아닙니다. 코드를 확인해주세요.</ErrorText>
      )}
      {applied && (
        <GiftNote>
          <GiftIcon>🎁</GiftIcon>
          <GiftText>
            <b>이용 후 특별한 혜택을 보내드립니다.</b>
            <br />
            결제 금액은 변동되지 않습니다.
          </GiftText>
        </GiftNote>
      )}
    </Wrapper>
  );
};

export default InfluencerCodeArea;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const Label = styled.span`
  ${({ theme }) => theme.FONT.B1};
  font-weight: 700;
`;

const InputRow = styled.div`
  display: flex;
  gap: 8px;
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  ${({ theme }) => theme.FONT.B2};
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.COLOR.GREY_05};
  border-radius: 10px;
  color: ${({ theme }) => theme.COLOR.GREY_01};

  &:focus {
    outline: none;
    border-color: ${getPrimaryColor(JEJURO)};
  }
`;

const ApplyButton = styled.button`
  flex: 0 0 auto;
  ${({ theme }) => theme.FONT.B2};
  font-weight: 700;
  padding: 0 18px;
  border: none;
  border-radius: 10px;
  background: ${getPrimaryColor(JEJURO)};
  color: ${({ theme }) => theme.COLOR.WHITE};
  cursor: pointer;
`;

const AppliedRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid ${getPrimaryColor(JEJURO)}55;
  border-radius: 10px;
  background: ${getPrimaryColor(JEJURO)}0d;
`;

const AppliedCode = styled.span`
  ${({ theme }) => theme.FONT.B2};
  font-weight: 700;
  color: ${getPrimaryColor(JEJURO)};
`;

const ClearButton = styled.button`
  flex: 0 0 auto;
  ${({ theme }) => theme.FONT.B3};
  color: ${({ theme }) => theme.COLOR.GREY_02};
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: underline;
`;

const ErrorText = styled.p`
  margin: 0;
  ${({ theme }) => theme.FONT.B3};
  color: #c0392b;
`;

const GiftNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 11px 12px;
  border-radius: 10px;
  background: #fdf6e7;
  border: 1px solid #e7b45e66;
`;

const GiftIcon = styled.span`
  font-size: 16px;
  line-height: 1.3;
`;

const GiftText = styled.span`
  ${({ theme }) => theme.FONT.B3};
  line-height: 1.55;
  color: ${({ theme }) => theme.COLOR.GREY_01};

  b {
    color: #8a6410;
  }
`;
